import React, { ReactNode, useMemo, useState } from 'react';
import CloseIcon from '@mui/icons-material/Close';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import FilterAltOutlinedIcon from '@mui/icons-material/FilterAltOutlined';
import HourglassTopIcon from '@mui/icons-material/HourglassTop';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormLabel,
  Menu,
  Pagination,
  Stack,
  Typography,
} from '@mui/material';
import { UseQueryResult } from '@tanstack/react-query';
import {
  EmptySectionMessage,
  FullPageLoader,
  TextInput,
} from '@tassomutuo-backoffice/ui';

import { LeadProductChip } from '../../../components';
import {
  ManagedLeadCategory,
  ManagedLeadLifecycle,
  ManagedLeadListResponse,
  ManagedLeadProductPath,
  ManagedLeadsUpdateRequestFilters,
} from '../../../services/api/managed-leads/managedLeads';
import { ManagedLeadFilters } from '../hooks/useManagedLeadListParams';
import TassoQuintoErrorAlert from './TassoQuintoErrorAlert';
import { ManagedLeadsUpdateRequestButton } from './ManagedLeadUpdateRequestButton';

const CATEGORY_PRESENTATION = {
  in_progress: {
    title: 'Lead in gestione',
    iconLabel: 'Categoria In gestione',
    Icon: HourglassTopIcon,
  },
  won: {
    title: 'Lead Vinte',
    iconLabel: 'Categoria Vinte',
    Icon: EmojiEventsIcon,
  },
  dropped: {
    title: 'Lead Dropped',
    iconLabel: 'Categoria Drop',
    Icon: CloseIcon,
  },
} satisfies Record<
  ManagedLeadCategory,
  { title: string; iconLabel: string; Icon: typeof HourglassTopIcon }
>;

interface ManagedLeadListPageContentProps<T> {
  product: ManagedLeadProductPath;
  loadingMessage: string;
  ownerMissingMessage: string;
  category: ManagedLeadCategory;
  searchLabel: string;
  hasActiveSearch: boolean;
  searchInput: string;
  setSearchInput: (value: string) => void;
  filters: ManagedLeadFilters;
  applyFilters: (filters: ManagedLeadFilters) => void;
  lifecycleQuery: UseQueryResult<ManagedLeadLifecycle, Error>;
  page: number;
  setPage: (page: number) => void;
  query: UseQueryResult<ManagedLeadListResponse<T>, Error>;
  /**
   * Filters of the aggregate Richiesta di aggiornamento; absent where the list
   * offers none, i.e. outside In gestione.
   */
  updateRequestFilters?: ManagedLeadsUpdateRequestFilters;
  renderLead: (lead: T) => ReactNode;
}

const formatStageKey = (stageKey: string) => {
  const readableKey = stageKey.replace(/[_-]+/g, ' ').trim();

  return readableKey
    ? `${readableKey.charAt(0).toUpperCase()}${readableKey.slice(1)}`
    : stageKey;
};

const ManagedLeadListPageContent = <T,>({
  product,
  loadingMessage,
  ownerMissingMessage,
  category,
  searchLabel,
  hasActiveSearch,
  searchInput,
  setSearchInput,
  filters,
  applyFilters,
  lifecycleQuery,
  page,
  setPage,
  query,
  updateRequestFilters,
  renderLead,
}: ManagedLeadListPageContentProps<T>) => {
  const [filterAnchor, setFilterAnchor] = useState<HTMLElement | null>(null);
  const [draftFilters, setDraftFilters] = useState<ManagedLeadFilters>({
    stages: [],
    needsUpdate: false,
  });
  const categoryPresentation = CATEGORY_PRESENTATION[category];
  const CategoryIcon = categoryPresentation.Icon;
  const inProgressStages = useMemo(
    () =>
      lifecycleQuery.data?.stages.filter(
        (stage) => stage.category === 'in_progress'
      ) ?? [],
    [lifecycleQuery.data?.stages]
  );
  const totalLabel =
    query.data?.totalResults === null
      ? 'Totale risultati non disponibile'
      : query.data?.totalResults === 1
      ? '1 risultato'
      : `${query.data?.totalResults ?? 0} risultati`;
  const hasAppliedFilters =
    filters.stages.length > 0 || filters.needsUpdate;
  const hasActiveCriteria = hasActiveSearch || hasAppliedFilters;
  const appliedFilterSummary = useMemo(() => {
    const parts: string[] = [];

    if (filters.stages.length === 1) {
      const selectedStage = inProgressStages.find(
        (stage) => stage.key === filters.stages[0]
      );
      parts.push(
        selectedStage?.label ?? formatStageKey(filters.stages[0])
      );
    } else if (filters.stages.length > 1) {
      parts.push(`${filters.stages.length} fasi selezionate`);
    }
    if (filters.needsUpdate) parts.push('Lead da aggiornare');

    return parts.join(', ');
  }, [filters.needsUpdate, filters.stages, inProgressStages]);

  const openFilterMenu = (event: React.MouseEvent<HTMLElement>) => {
    setDraftFilters(filters);
    setFilterAnchor(event.currentTarget);
  };

  const closeFilterMenu = () => {
    setFilterAnchor(null);
    setDraftFilters(filters);
  };

  const toggleDraftStage = (stage: string) => {
    setDraftFilters((current) => ({
      ...current,
      stages: current.stages.includes(stage)
        ? current.stages.filter((candidate) => candidate !== stage)
        : [...current.stages, stage],
    }));
  };

  const commitDraftFilters = () => {
    applyFilters(draftFilters);
    setFilterAnchor(null);
  };

  const resetFilters = () => {
    const emptyFilters = { stages: [], needsUpdate: false };
    applyFilters(emptyFilters);
    setDraftFilters(emptyFilters);
    setFilterAnchor(null);
  };

  return (
    <Box px={{ xs: 2, md: 4 }} py={4}>
      <Box mb={4}>
        <Stack direction="row" alignItems="center" flexWrap="wrap" gap={1}>
          <CategoryIcon
            fontSize="large"
            role="img"
            titleAccess={categoryPresentation.iconLabel}
          />
          <Typography component="h1" variant="h4" fontWeight="bold">
            {categoryPresentation.title}
          </Typography>
          <LeadProductChip product={product} />
        </Stack>
      </Box>

      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        alignItems={{ xs: 'stretch', sm: 'center' }}
        justifyContent="space-between"
        gap={2}
        mb={3}
      >
        <Box>
          {hasAppliedFilters && (
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              alignItems={{ xs: 'flex-start', sm: 'center' }}
              gap={1}
            >
              <Typography fontWeight="bold">Filtri applicati:</Typography>
              <Typography>{appliedFilterSummary}</Typography>
              <Button variant="contained" onClick={resetFilters}>
                Resetta filtri
              </Button>
            </Stack>
          )}
        </Box>

        <Stack
          direction={{ xs: 'column-reverse', sm: 'row' }}
          alignItems={{ xs: 'stretch', sm: 'center' }}
          gap={2}
        >
          {/* Hidden on an empty selection: the server would refuse it. */}
          {updateRequestFilters && Boolean(query.data?.results.length) && (
            <ManagedLeadsUpdateRequestButton
              product={product}
              filters={updateRequestFilters}
            />
          )}
          {category === 'in_progress' && (
            <Button
              variant={hasAppliedFilters ? 'contained' : 'text'}
              onClick={openFilterMenu}
              startIcon={<FilterAltOutlinedIcon fontSize="small" />}
            >
              Filtra
            </Button>
          )}
          <Box sx={{ width: '100%', maxWidth: '300px' }}>
            <TextInput
              type="search"
              placeholder="Cerca..."
              value={searchInput}
              inputProps={{ 'aria-label': searchLabel }}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                setSearchInput(event.target.value)
              }
            />
          </Box>
        </Stack>
      </Stack>

      {category === 'in_progress' && (
        <Menu
          anchorEl={filterAnchor}
          open={Boolean(filterAnchor)}
          onClose={closeFilterMenu}
        >
          <Box sx={{ p: 2, width: { xs: 280, sm: 340 }, maxWidth: '90vw' }}>
            <Typography fontWeight="bold">Filtra Lead</Typography>
            <FormControl
              component="fieldset"
              variant="standard"
              fullWidth
              sx={{ mt: 2 }}
            >
              <FormLabel component="legend">Fase lead</FormLabel>
              {lifecycleQuery.isPending && (
                <Typography variant="body2">Caricamento fasi...</Typography>
              )}
              {lifecycleQuery.isError && (
                <TassoQuintoErrorAlert
                  error={lifecycleQuery.error}
                  retry={() => lifecycleQuery.refetch()}
                  retryableMessage="Non è stato possibile caricare le fasi su cui filtrare."
                  unauthorizedMessage="Non sei autorizzato a filtrare queste lead per fase."
                  notFoundMessage="Le fasi di questo prodotto non sono disponibili."
                  ownerMissingMessage="Non ci sono fasi da mostrare senza un owner HubSpot associato."
                />
              )}
              <FormGroup>
                {inProgressStages.map((stage) => (
                  <FormControlLabel
                    key={stage.key}
                    control={
                      <Checkbox
                        checked={draftFilters.stages.includes(stage.key)}
                        onChange={() => toggleDraftStage(stage.key)}
                      />
                    }
                    label={stage.label}
                  />
                ))}
              </FormGroup>
            </FormControl>
            <FormControlLabel
              control={
                <Checkbox
                  checked={draftFilters.needsUpdate}
                  onChange={(event) =>
                    setDraftFilters((current) => ({
                      ...current,
                      needsUpdate: event.target.checked,
                    }))
                  }
                />
              }
              label="Lead da aggiornare"
            />
            <Button
              type="button"
              variant="contained"
              fullWidth
              sx={{ mt: 3 }}
              onClick={commitDraftFilters}
            >
              APPLICA FILTRI
            </Button>
            {hasAppliedFilters && (
              <Button
                type="button"
                fullWidth
                sx={{ mt: 1 }}
                onClick={resetFilters}
              >
                RIMUOVI FILTRI
              </Button>
            )}
          </Box>
        </Menu>
      )}

      <FullPageLoader show={query.isPending} />
      {query.isPending && (
        <Box
          component="span"
          role="status"
          sx={{
            position: 'absolute',
            width: 1,
            height: 1,
            p: 0,
            m: -1,
            overflow: 'hidden',
            clip: 'rect(0 0 0 0)',
            whiteSpace: 'nowrap',
            border: 0,
          }}
        >
          {loadingMessage}
        </Box>
      )}

      {query.isError && (
        <TassoQuintoErrorAlert
          error={query.error}
          retry={() => query.refetch()}
          retryableMessage="Non è stato possibile caricare le lead. Riprova."
          unauthorizedMessage="Non sei autorizzato a vedere queste lead."
          notFoundMessage="Le lead di questo prodotto non sono disponibili."
          ownerMissingMessage={ownerMissingMessage}
        />
      )}

      {query.data?.results.length === 0 && (
        <EmptySectionMessage
          title={
            hasActiveCriteria
              ? 'La ricerca non ha prodotto risultati'
              : 'Non ci sono lead da mostrare'
          }
        />
      )}

      {query.data && (
        <>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            justifyContent="space-between"
            mb={2}
          >
            <Typography>{totalLabel}</Typography>
            <Typography color="text.secondary" variant="body2">
              {category === 'in_progress'
                ? 'Attività meno recente per prima'
                : 'Ingresso più recente nella fase'}
            </Typography>
          </Stack>

          {query.data.resultLimitReached && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              Il limite massimo di risultati è stato raggiunto. Restringi la
              ricerca o i filtri per ottenere un elenco completo.
            </Alert>
          )}

          <Stack spacing={2}>{query.data.results.map(renderLead)}</Stack>

          {query.data.totalPages !== null && query.data.totalPages > 1 && (
            <Box display="flex" justifyContent="center" py={3}>
              <Pagination
                page={page}
                count={query.data.totalPages}
                color="primary"
                onChange={(_, nextPage) => setPage(nextPage)}
              />
            </Box>
          )}
        </>
      )}
    </Box>
  );
};

export default ManagedLeadListPageContent;
